import { calendarPriorPeriodWindow, isCalendarPeriodWindow } from './metric-period-calendar';

/**
 * Period-over-period derivation for governed metric facts (issue #5067 era:
 * one generic derive algebra in the shared engine, never per-module compare
 * code). Pure functions: rows in, deltas out, no I/O and no clock.
 */

export type MetricComparePeriodWindow = Readonly<{
  type: string;
  from: string;
  to: string;
}>;

export type MetricCompareRow = Readonly<{
  metricKey: string;
  periodKey: string;
  dimensions: Readonly<Record<string, string>>;
  /** Decimal string as returned by the metric query executor. */
  valueNumeric: string;
  unit: string;
}>;

export type MetricPeriodDeltaVerdict = 'improved' | 'declined' | 'flat' | 'not_comparable';

export type MetricPeriodDelta = Readonly<{
  metricKey: string;
  dimensions: Readonly<Record<string, string>>;
  unit: string;
  basePeriodKey: string;
  priorPeriodKey: string | null;
  baseValue: number;
  priorValue: number | null;
  deltaValue: number | null;
  /** Signed fraction of |prior| (0.077 = +7.7%); null when prior is 0/absent. */
  deltaFraction: number | null;
  verdict: MetricPeriodDeltaVerdict;
}>;

export type MetricDirectionResolver = (metricKey: string) => string | null | undefined;

/**
 * The prior window of equal length immediately before the requested window.
 * Supported only for purely numeric period keys (fiscal_year '2025',
 * fiscal_quarter would need calendar arithmetic and is refused with null so
 * callers fail closed with a named reason instead of guessing).
 */
export function priorPeriodWindow(
  period: MetricComparePeriodWindow
): MetricComparePeriodWindow | null {
  if (isCalendarPeriodWindow(period)) {
    return calendarPriorPeriodWindow(period);
  }

  const from = Number.parseInt(period.from, 10);
  const to = Number.parseInt(period.to, 10);
  if (
    !Number.isSafeInteger(from) ||
    !Number.isSafeInteger(to) ||
    String(from) !== period.from.trim() ||
    String(to) !== period.to.trim() ||
    to < from
  ) {
    return null;
  }
  const length = to - from + 1;
  return {
    type: period.type,
    from: String(from - length),
    to: String(to - length),
  };
}

export function metricPeriodDeltaCellKey(
  row: Pick<MetricCompareRow, 'metricKey' | 'dimensions'>
): string {
  const dimensionKey = Object.keys(row.dimensions ?? {})
    .sort()
    .map((key) => `${key}=${row.dimensions[key]}`)
    .join('|');
  return `${row.metricKey}|${dimensionKey}`;
}

function latestRowPerCell(rows: readonly MetricCompareRow[]): Map<string, MetricCompareRow> {
  const byCell = new Map<string, MetricCompareRow>();
  for (const row of rows) {
    const key = metricPeriodDeltaCellKey(row);
    const existing = byCell.get(key);
    if (!existing || row.periodKey > existing.periodKey) {
      byCell.set(key, row);
    }
  }
  return byCell;
}

function finiteValue(raw: string): number | null {
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}

function verdictFor(delta: number, direction: string | null | undefined): MetricPeriodDeltaVerdict {
  if (delta === 0) return 'flat';
  if (direction === 'higher_is_better') {
    return delta > 0 ? 'improved' : 'declined';
  }
  if (direction === 'lower_is_better') {
    return delta < 0 ? 'improved' : 'declined';
  }
  return 'not_comparable';
}

/**
 * Join base-window rows to prior-window rows by (metricKey, dimensions) and
 * derive signed deltas with a direction-aware verdict. A base cell with no
 * prior counterpart is reported as not_comparable rather than dropped, so the
 * gap is visible instead of silently shrinking the readout.
 */
export function deriveMetricPeriodDeltas(input: {
  baseRows: readonly MetricCompareRow[];
  priorRows: readonly MetricCompareRow[];
  direction: MetricDirectionResolver;
}): MetricPeriodDelta[] {
  const priorByCell = latestRowPerCell(input.priorRows);
  const deltas: MetricPeriodDelta[] = [];
  for (const [cellKey, baseRow] of latestRowPerCell(input.baseRows)) {
    const baseValue = finiteValue(baseRow.valueNumeric);
    if (baseValue == null) continue;
    const priorRow = priorByCell.get(cellKey) ?? null;
    const priorValue = priorRow == null ? null : finiteValue(priorRow.valueNumeric);
    if (priorRow == null || priorValue == null) {
      deltas.push({
        metricKey: baseRow.metricKey,
        dimensions: baseRow.dimensions,
        unit: baseRow.unit,
        basePeriodKey: baseRow.periodKey,
        priorPeriodKey: priorRow?.periodKey ?? null,
        baseValue,
        priorValue: null,
        deltaValue: null,
        deltaFraction: null,
        verdict: 'not_comparable',
      });
      continue;
    }
    const deltaValue = baseValue - priorValue;
    deltas.push({
      metricKey: baseRow.metricKey,
      dimensions: baseRow.dimensions,
      unit: baseRow.unit,
      basePeriodKey: baseRow.periodKey,
      priorPeriodKey: priorRow.periodKey,
      baseValue,
      priorValue,
      deltaValue,
      deltaFraction: priorValue === 0 ? null : deltaValue / Math.abs(priorValue),
      verdict: verdictFor(deltaValue, input.direction(baseRow.metricKey)),
    });
  }
  return deltas.sort((a, b) =>
    metricPeriodDeltaCellKey(a) < metricPeriodDeltaCellKey(b) ? -1 : 1
  );
}
